import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loanAPI } from '../services/api';
import PaymentMethodModal from './payment/PaymentMethodModal';
import './StudentPayments.css';

interface StudentPayment {
  id: string;
  loanId: string;
  loanNumber: string;
  amount: number;
  dueDate?: string;
  paidDate?: string;
  status: 'pending' | 'completed' | 'failed' | 'cancelled' | 'refunded';
  paymentMethod?: string;
  reference?: string;
  loanStatus?: string;
  createdAt: string;
  updatedAt: string;
}

interface StudentLoan {
  id: string;
  loanNumber: string;
  principalAmount: number;
  totalAmount: number;
  totalPaid?: number;
  outstandingBalance?: number;
  status: string;
  repaymentDate?: string;
  createdAt: string;
}

interface PaymentSummary {
  totalPending: number;
  totalCompleted: number;
  totalFailed: number;
  pendingAmount: number;
  completedAmount: number;
  totalPayments: number;
}

const PAGE_SIZE = 5;

const Icons = {
  BackArrow: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  ),
  Clock: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  CheckCircle: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  ),
  Chart: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  ),
  EmptyWallet: () => (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
      <path d="M4 6v12c0 1.1.9 2 2 2h14v-4" />
      <path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4h-4z" />
    </svg>
  ),
  XCircle: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  ),
  Search: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  ),
  ChevronRight: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  ),
  ChevronLeft: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  ),
  Download: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  ),
  CreditCard: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </svg>
  ),
  Refresh: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
};

const methodLabel = (method?: string) => {
  switch (method) {
    case 'payfast':
      return 'PayFast';
    case 'debit_order':
    case 'debicheck':
      return 'DebiCheck';
    case 'manual':
      return 'Manual';
    default:
      return method || '—';
  }
};

const StudentPayments: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusLoanId = searchParams.get('loanId') || '';
  const statusFromUrl = searchParams.get('status');

  const [payments, setPayments] = useState<StudentPayment[]>([]);
  const [loans, setLoans] = useState<StudentLoan[]>([]);
  const [summary, setSummary] = useState<PaymentSummary>({
    totalPending: 0,
    totalCompleted: 0,
    totalFailed: 0,
    pendingAmount: 0,
    completedAmount: 0,
    totalPayments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>(
    statusFromUrl === 'pending' ||
      statusFromUrl === 'completed' ||
      statusFromUrl === 'failed' ||
      statusFromUrl === 'all'
      ? statusFromUrl
      : 'all'
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedPaymentForModal, setSelectedPaymentForModal] = useState<{ id: string; amount: number } | null>(null);

  useEffect(() => {
    fetchPayments();
    const interval = setInterval(fetchPayments, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (
      statusFromUrl === 'pending' ||
      statusFromUrl === 'completed' ||
      statusFromUrl === 'failed' ||
      statusFromUrl === 'all'
    ) {
      setFilterStatus(statusFromUrl);
    }
  }, [statusFromUrl]);

  useEffect(() => {
    setPage(1);
  }, [filterStatus, searchTerm, focusLoanId]);

  // After load, if we were sent here for a specific loan's pending payment, open pay modal
  useEffect(() => {
    if (loading || !focusLoanId) return;
    const actionable = payments.find(
      (p) =>
        p.loanId === focusLoanId &&
        (p.status === 'pending' || p.status === 'failed') &&
        ['disbursed', 'overdue'].includes(
          loans.find((l) => l.id === p.loanId)?.status || p.loanStatus || ''
        )
    );
    if (actionable) {
      setSelectedPaymentForModal({ id: actionable.id, amount: actionable.amount });
      setIsPaymentModalOpen(true);
      // Clear loan focus from URL so refresh doesn't keep reopening the modal
      const next = new URLSearchParams(searchParams);
      next.delete('loanId');
      setSearchParams(next, { replace: true });
    }
  }, [loading, focusLoanId, payments, loans, searchParams, setSearchParams]);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await loanAPI.getStudentPayments();

      if (response.success && response.data) {
        const typedPayments: StudentPayment[] = response.data.payments.map((payment) => ({
          ...payment,
          status: payment.status as StudentPayment['status'],
        }));
        setPayments(typedPayments);
        setLoans(response.data.loans);
        setSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Error fetching student payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredPayments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const resolveLoanStatus = (payment: StudentPayment) =>
      loans.find((l) => l.id === payment.loanId)?.status || payment.loanStatus || '';

    // Actionable items first on All (and any mixed list): settleable pending →
    // retryable failed → other pending/failed → completed → the rest.
    const sortRank = (payment: StudentPayment) => {
      const loanStatus = resolveLoanStatus(payment);
      const actionable = ['disbursed', 'overdue'].includes(loanStatus);
      if (payment.status === 'pending' && actionable) return 0;
      if (payment.status === 'failed' && actionable) return 1;
      if (payment.status === 'pending') return 2;
      if (payment.status === 'failed') return 3;
      if (payment.status === 'completed') return 4;
      return 5;
    };

    return payments
      .filter((payment) => {
        const matchesStatus = filterStatus === 'all' || payment.status === filterStatus;
        const matchesLoan = !focusLoanId || payment.loanId === focusLoanId;
        const matchesSearch =
          !term ||
          payment.loanNumber?.toLowerCase().includes(term) ||
          payment.reference?.toLowerCase().includes(term) ||
          payment.id.toLowerCase().includes(term);
        return matchesStatus && matchesLoan && matchesSearch;
      })
      .sort((a, b) => {
        const rankDiff = sortRank(a) - sortRank(b);
        if (rankDiff !== 0) return rankDiff;
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
  }, [payments, loans, filterStatus, searchTerm, focusLoanId]);

  const getLoanStatus = (payment: StudentPayment) =>
    loans.find((l) => l.id === payment.loanId)?.status || payment.loanStatus || '';

  const totalPages = Math.max(1, Math.ceil(filteredPayments.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pagePayments = filteredPayments.slice(pageStart, pageStart + PAGE_SIZE);
  const showingFrom = filteredPayments.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + PAGE_SIZE, filteredPayments.length);

  const handlePaymentClick = (paymentId: string, amount: number, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedPaymentForModal({ id: paymentId, amount });
    setIsPaymentModalOpen(true);
  };

  const handlePaymentMethodSelect = (method: 'payfast' | 'debit_order') => {
    if (!selectedPaymentForModal) return;
    setIsPaymentModalOpen(false);
    if (method === 'payfast') {
      navigate(`/payment/${selectedPaymentForModal.id}/payfast`);
    } else {
      navigate('/payment/debit-order/setup');
    }
  };

  const exportStatement = () => {
    const rows = [
      ['Loan', 'Amount', 'Due Date', 'Paid Date', 'Reference', 'Method', 'Status'],
      ...filteredPayments.map((p) => [
        p.loanNumber,
        String(p.amount),
        p.dueDate || '',
        p.paidDate || '',
        p.reference || '',
        methodLabel(p.paymentMethod),
        p.status,
      ]),
    ];
    const csv = rows
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `genlend-payment-history-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const statusIcon = (status: string) => {
    if (status === 'completed') return <Icons.CheckCircle />;
    if (status === 'pending') return <Icons.Clock />;
    return <Icons.XCircle />;
  };

  if (loading && payments.length === 0) {
    return (
      <div className="student-payments">
        <div className="payments-loading">
          <div className="loading-spinner" />
          <p>Loading payment history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="student-payments">
      <div className="payments-topbar">
        <button type="button" className="back-nav-btn" onClick={() => navigate('/dashboard')}>
          <Icons.BackArrow /> Back to Dashboard
        </button>
      </div>

      <div className="payments-header">
        <div>
          <h1>Payment History</h1>
          <p>Track your loans and repayments</p>
        </div>
        <button type="button" className="export-btn" onClick={exportStatement} disabled={filteredPayments.length === 0}>
          <Icons.Download /> Export Statement
        </button>
      </div>

      <div className="payment-summary-cards">
        <div className="ph-stat-card ph-stat-card--pending">
          <div className="ph-stat-icon" aria-hidden="true">
            <Icons.Clock />
          </div>
          <div className="ph-stat-body">
            <div className="ph-stat-label">Pending Payments</div>
            <div className="ph-stat-value">{summary.totalPending}</div>
            <div className="ph-stat-meta">{formatCurrency(summary.pendingAmount)}</div>
          </div>
        </div>

        <div className="ph-stat-card ph-stat-card--completed">
          <div className="ph-stat-icon" aria-hidden="true">
            <Icons.CheckCircle />
          </div>
          <div className="ph-stat-body">
            <div className="ph-stat-label">Completed</div>
            <div className="ph-stat-value">{summary.totalCompleted}</div>
            <div className="ph-stat-meta">{formatCurrency(summary.completedAmount)}</div>
          </div>
        </div>

        <div className="ph-stat-card ph-stat-card--total">
          <div className="ph-stat-icon" aria-hidden="true">
            <Icons.Chart />
          </div>
          <div className="ph-stat-body">
            <div className="ph-stat-label">Total Payments</div>
            <div className="ph-stat-value">{summary.totalPayments}</div>
            <div className="ph-stat-meta">All time</div>
          </div>
        </div>
      </div>

      <div className="payments-toolbar">
        <div className="payments-filter">
          <button type="button" className={filterStatus === 'all' ? 'active' : ''} onClick={() => setFilterStatus('all')}>
            All ({payments.length})
          </button>
          <button type="button" className={filterStatus === 'pending' ? 'active' : ''} onClick={() => setFilterStatus('pending')}>
            Pending ({summary.totalPending})
          </button>
          <button type="button" className={filterStatus === 'completed' ? 'active' : ''} onClick={() => setFilterStatus('completed')}>
            Completed ({summary.totalCompleted})
          </button>
          <button type="button" className={filterStatus === 'failed' ? 'active' : ''} onClick={() => setFilterStatus('failed')}>
            Failed ({summary.totalFailed})
          </button>
        </div>

        <div className="payments-search">
          <Icons.Search />
          <input
            type="search"
            placeholder="Search by reference or loan ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            aria-label="Search payments"
          />
        </div>
      </div>

      <div className="payments-list">
        {pagePayments.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Icons.EmptyWallet />
            </div>
            <h3>No payments found</h3>
            <p>
              {filterStatus === 'all' && !searchTerm
                ? "You don't have any payments yet. Payments will appear here once your loan is approved."
                : 'No payments match your current filters.'}
            </p>
          </div>
        ) : (
          pagePayments.map((payment) => {
            const isExpanded = expandedId === payment.id;
            return (
              <div
                key={payment.id}
                className={`sp-payment-row sp-payment-row--${payment.status} ${isExpanded ? 'is-expanded' : ''}`}
                onClick={() => setExpandedId(isExpanded ? null : payment.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(isExpanded ? null : payment.id);
                  }
                }}
              >
                <div className={`sp-payment-row-accent sp-payment-row-accent--${payment.status}`} />

                <div className={`sp-payment-row-icon sp-payment-row-icon--${payment.status}`}>
                  {statusIcon(payment.status)}
                </div>

                <div className="sp-payment-row-main">
                  <div className="sp-payment-row-loan">Loan: {payment.loanNumber}</div>
                  <div className="sp-payment-row-amount">{formatCurrency(payment.amount)}</div>
                </div>

                <div className="sp-payment-row-fields">
                  <div className="payment-field">
                    <span className="payment-field-label">Due Date</span>
                    <span className="payment-field-value">{formatDate(payment.dueDate)}</span>
                  </div>
                  <div className="payment-field">
                    <span className="payment-field-label">Paid Date</span>
                    <span className="payment-field-value">{payment.paidDate ? formatDate(payment.paidDate) : '—'}</span>
                  </div>
                  <div className="payment-field">
                    <span className="payment-field-label">Reference</span>
                    <span className="payment-field-value payment-field-value--mono">
                      {payment.reference || payment.id.slice(0, 12)}
                    </span>
                  </div>
                  <div className="payment-field">
                    <span className="payment-field-label">Method</span>
                    <span className="payment-field-value">{methodLabel(payment.paymentMethod)}</span>
                  </div>
                </div>

                <div className="sp-payment-row-actions" onClick={(e) => e.stopPropagation()}>
                  <span className={`status-pill status-pill--${payment.status}`}>
                    {payment.status}
                  </span>
                  {payment.status === 'pending' &&
                    ['disbursed', 'overdue'].includes(getLoanStatus(payment)) && (
                    <button
                      type="button"
                      className="pay-now-btn"
                      onClick={(e) => handlePaymentClick(payment.id, payment.amount, e)}
                    >
                      <Icons.CreditCard /> Make Payment
                    </button>
                  )}
                  {payment.status === 'failed' &&
                    ['disbursed', 'overdue'].includes(getLoanStatus(payment)) && (
                    <button
                      type="button"
                      className="pay-now-btn pay-now-btn--retry"
                      onClick={(e) => handlePaymentClick(payment.id, payment.amount, e)}
                    >
                      <Icons.Refresh /> Retry Payment
                    </button>
                  )}
                </div>

                <div className={`sp-payment-row-chevron ${isExpanded ? 'open' : ''}`}>
                  <Icons.ChevronRight />
                </div>

                {isExpanded && (
                  <div className="sp-payment-row-expand">
                    <div>
                      <span className="payment-field-label">Payment ID</span>
                      <span className="payment-field-value payment-field-value--mono">{payment.id}</span>
                    </div>
                    <div>
                      <span className="payment-field-label">Updated</span>
                      <span className="payment-field-value">{formatDate(payment.updatedAt || payment.createdAt)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {filteredPayments.length > 0 && (
        <div className="payments-pagination">
          <span className="pagination-meta">
            Showing {showingFrom} to {showingTo} of {filteredPayments.length} payments
          </span>
          <div className="pagination-controls">
            <button
              type="button"
              className="page-btn"
              disabled={currentPage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              aria-label="Previous page"
            >
              <Icons.ChevronLeft />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((n) => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
              .reduce<(number | 'ellipsis')[]>((acc, n, idx, arr) => {
                if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                acc.push(n);
                return acc;
              }, [])
              .map((item, idx) =>
                item === 'ellipsis' ? (
                  <span key={`e-${idx}`} className="page-ellipsis">
                    …
                  </span>
                ) : (
                  <button
                    key={item}
                    type="button"
                    className={`page-btn ${currentPage === item ? 'active' : ''}`}
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              type="button"
              className="page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              aria-label="Next page"
            >
              <Icons.ChevronRight />
            </button>
          </div>
        </div>
      )}

      {loans.length > 0 && (
        <div className="loans-summary">
          <div className="loans-summary-header">
            <h3>My Loans</h3>
            <span className="loans-active-count">
              {loans.filter((l) => {
                const owing = l.outstandingBalance ?? Math.max(0, l.totalAmount - (l.totalPaid || 0));
                return owing > 0.01 && ['approved', 'disbursed', 'overdue'].includes(l.status);
              }).length}{' '}
              active with balance owing
            </span>
          </div>
          <div className="loans-list">
            {loans.map((loan) => {
              const outstanding =
                loan.outstandingBalance ??
                Math.max(0, loan.totalAmount - (loan.totalPaid || 0));
              const isActive =
                outstanding > 0.01 &&
                ['approved', 'disbursed', 'overdue', 'defaulted'].includes(loan.status);
              const statusLabel =
                loan.status === 'repaid' || outstanding <= 0.01
                  ? 'Paid off'
                  : loan.status === 'disbursed' || loan.status === 'approved'
                    ? 'Active'
                    : loan.status === 'pending'
                      ? 'Under review'
                      : loan.status === 'overdue'
                        ? 'Overdue'
                        : loan.status;

              return (
                <div
                  key={loan.id}
                  className={`loan-card ${isActive ? 'loan-card--active' : 'loan-card--closed'}`}
                >
                  <div className="loan-header">
                    <div className="loan-number">{loan.loanNumber}</div>
                    <div className={`loan-status loan-status--${isActive ? 'active' : 'closed'}`}>
                      {statusLabel}
                    </div>
                  </div>
                  <div className="loan-details">
                    <div className="loan-detail">
                      <span>Total</span>
                      <strong>{formatCurrency(loan.totalAmount)}</strong>
                    </div>
                    <div className="loan-detail">
                      <span>Outstanding</span>
                      <strong className={outstanding > 0.01 ? 'loan-owing' : 'loan-clear'}>
                        {formatCurrency(Math.max(0, outstanding))}
                      </strong>
                    </div>
                    {loan.repaymentDate && (
                      <div className="loan-detail">
                        <span>Repayment Date</span>
                        <strong>{formatDate(loan.repaymentDate)}</strong>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <PaymentMethodModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSelectMethod={handlePaymentMethodSelect}
        amount={selectedPaymentForModal?.amount || 0}
      />
    </div>
  );
};

export default StudentPayments;
